import * as SentryReact from '@sentry/react';
import { isAxiosError } from 'axios';
import { create } from 'zustand';

import { amplitudeModule } from '@/modules/amplitude';
import api, {
  ApiUserType,
  ApiV2UserSubscriptionType,
  apiV1GetMe,
  apiV1GetSubscription,
  apiV1PostMeVendors,
} from '@/modules/api';
import { deviceModule } from '@/modules/device';
import { EV_EM_SUBSCRIPTION_KEY, eventEmitter } from '@/modules/event-emitter';
import {
  WebCoinsPlanType,
  WebSubscriptionPlanType,
} from '@/modules/subscriptions';
import { TikTokModule } from '@/modules/tiktok';
import { webCampaign } from '@/modules/webCampaign';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { getFbCookie } from '@/utils/getFbCookie';
import { getGaCookie } from '@/utils/getGaCookie';

interface UseCurrentUserStoreType {
  user: ApiUserType | null;
  deviceId: string | null;
  id: string | null;
  name: string | null;
  email: string | null;
  onboarded: boolean | null;
  registered: boolean | null;
  selectedCategories: ApiUserType['categories'];
  subscription: ApiV2UserSubscriptionType | null;
  selectedPlanId: WebSubscriptionPlanType;
  selectedCoinPlanId: WebCoinsPlanType | null;
  setSelectedPlanId: (planId: WebSubscriptionPlanType) => void;
  setSelectedCoinPlanId: (coinPlanId: WebCoinsPlanType | null) => void;
  setName: (name: string) => void;
}

export const useUserStore = create<UseCurrentUserStoreType>((set) => ({
  user: null,
  deviceId: deviceModule.id,
  id: null,
  name: null,
  email: null,
  onboarded: null,
  registered: null,
  categories: [],
  subscription: null,
  selectedCategories: [],
  selectedPlanId: webCampaign.setup.defaultPlan,
  selectedCoinPlanId: null,
  setSelectedPlanId(planId) {
    set({ selectedPlanId: planId });
  },
  setSelectedCoinPlanId(coinPlanId) {
    set({ selectedCoinPlanId: coinPlanId });
  },
  setName: (name: string) => set({ name }),
}));

export const useAccount = () => {
  const currentUser = useUserStore((state) => state);
  const userState = useUserStore((state) => state.user);
  const selectedPlanId = useUserStore((state) => state.selectedPlanId);
  const selectedCoinPlanId = useUserStore((state) => state.selectedCoinPlanId);
  const setSelectedPlanId = useUserStore((state) => state.setSelectedPlanId);
  const setSelectedCoinPlanId = useUserStore(
    (state) => state.setSelectedCoinPlanId,
  );
  const setName = useUserStore((state) => state.setName);

  const retrieveOrCreateUserByDeviceId = async (): Promise<string> => {
    const userRegistrationParams = {
      platform: webCampaign.getRegistrationPlatform(),
      from_web_onboarding: true,
      pheon_campaign: webCampaign.getKey(),
      ...(webCampaign.name && {
        web_onb_ab_test_name: webCampaign.name,
      }),
    };

    const { fbc, fbp } = getFbCookie();
    const { gaUserPseudoId } = getGaCookie();

    try {
      const { user } = await userService.createOrUpdate({
        ...userRegistrationParams,
        ...(fbc && { fbc }),
        ...(fbp && { fbp }),
        ...(gaUserPseudoId && { ga_user_pseudo_id: gaUserPseudoId }),
      });

      return user.id;
    } catch (error) {
      SentryReact.captureException(error, {
        tags: {
          context: 'first_user_registration_failed',
          pheon_campaign: webCampaign.getKey(),
          platform: webCampaign.getRegistrationPlatform(),
          ...(isAxiosError(error) && {
            httpStatus: String(error.response?.status ?? ''),
            errorCode: error.code ?? '',
          }),
        },
        extra: {
          deviceId: deviceModule.id,
          webCampaignName: webCampaign.name ?? undefined,
          message: error instanceof Error ? error.message : String(error),
          ...(isAxiosError(error) && {
            responseStatus: error.response?.status,
            responseData: error.response?.data,
          }),
        },
      });
      throw error;
    }
  };

  const refreshUser = async (
    onBoarding = true,
  ): Promise<ApiUserType | null> => {
    try {
      const [meData, subscriptionData] = await Promise.all([
        apiV1GetMe(),
        apiV1GetSubscription(),
      ]);

      if ((userState?.balance || 0) < (meData?.user.balance || 0)) {
        eventEmitter.emit(EV_EM_SUBSCRIPTION_KEY.COINS_BALANCE_INCREASE);
        console.log(
          `Event emitted: ${EV_EM_SUBSCRIPTION_KEY.COINS_BALANCE_INCREASE} from useAccount refreshUser`,
        );
      }

      useUserStore.setState({
        deviceId: deviceModule.id,
        id: meData.user.id,
        name: meData.user.name,
        email: meData.user.email,
        onboarded: onBoarding ? meData.user.is_onboarded : false,
        registered: meData.user.is_registered,
        selectedCategories: meData.user.selected_categories,
        subscription: subscriptionData?.subscription ?? null,
        user: meData.user,
      });

      return meData.user;
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }

    return null;
  };

  const initializeUser = async (token: string): Promise<ApiUserType | null> => {
    api.authorize(token);

    const { fbc, fbp } = getFbCookie();
    const { gaUserPseudoId } = getGaCookie();

    await apiV1PostMeVendors({
      vendor_id: deviceModule.id,
    });

    const user = await refreshUser();

    if (user && (fbc || fbp || gaUserPseudoId)) {
      userService.createOrUpdate({
        ...(fbc && { fbc }),
        ...(fbp && { fbp }),
        ...(gaUserPseudoId && { ga_user_pseudo_id: gaUserPseudoId }),
      });
    }

    if (user && canLogEvents()) {
      amplitudeModule.init(user.id);
      TikTokModule.identify({
        external_id: user.id,
        ...(user.email ? { email: user.email.toLowerCase().trim() } : {}),
      });
    }

    return user;
  };

  const signIn = async (token?: string | null) => {
    if (!token) {
      token = await retrieveOrCreateUserByDeviceId();
    }

    const user = await initializeUser(token);

    return user;
  };

  return {
    currentUser,
    user: userState,
    refreshUser,
    signIn,
    setName,
    selectedPlanId,
    selectedCoinPlanId,
    setSelectedPlanId,
    setSelectedCoinPlanId,
  };
};
